// Admin · Alerts tab — list of security alerts with per-item actions.
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldAlert, Check, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { API } from "../../../lib/api";

const SEV = {
  critica: { fg: "#ff3b5c", bg: "rgba(255,59,92,0.12)" },
  alta:    { fg: "#ff8c42", bg: "rgba(255,140,66,0.12)" },
  media:   { fg: "#f0c800", bg: "rgba(240,200,0,0.12)" },
  baja:    { fg: "#00d084", bg: "rgba(0,208,132,0.12)" },
};

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString([], { dateStyle: "short", timeStyle: "short" }); }
  catch { return "—"; }
};

export default function AlertsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [acting, setActing] = useState(null); // id currently being acted on
  const [filterTipo, setFilterTipo] = useState(null); // null = all types

  const reload = useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API}/admin/security/alerts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.clone().json())
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const handleAction = async (alertId, kind) => {
    setActing(alertId);
    try {
      const endpoint = kind === "revoke"
        ? `${API}/admin/security/alerts/${alertId}/auto-revoke-pro`
        : `${API}/admin/security/alerts/${alertId}/resolve`;
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.clone().json().catch(() => ({}));
      if (res.ok) {
        toast(
          kind === "revoke"
            ? `🗑️ PRO revocado · ${body.user_email_masked || alertId}`
            : "✓ Alerta resuelta",
          { duration: 4000 }
        );
        reload();
      } else {
        toast(`⚠️ ${body.detail || "Error"}`, { duration: 5000 });
      }
    } catch (e) {
      toast(`❌ ${String(e)}`, { duration: 5000 });
    } finally {
      setActing(null);
    }
  };

  return (
    <div data-testid="admin-tab-alerts" className="space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[2px] text-white/55">
            Alertas activas · last 24h críticas: {data?.total_criticas_24h ?? "—"}
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          data-testid="admin-alerts-reload"
          disabled={loading}
          className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2.5 py-1 font-mono text-[10px] text-white/70 hover:border-white/30 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refrescar
        </button>
      </div>

      {err && <p className="font-mono text-[11px] text-[#ff3b5c]">Error: {err}</p>}

      {/* Tipo filter chips — derived from active alerts. */}
      {data && (data.alertas_activas || []).length > 0 && (() => {
        const tipos = Array.from(new Set((data.alertas_activas || []).map((a) => a.tipo)));
        return (
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setFilterTipo(null)}
              data-testid="admin-alerts-filter-all"
              className="rounded-md px-2.5 py-1 font-mono text-[9px] font-bold transition-all"
              style={filterTipo === null
                ? { background: "rgba(249,115,22,0.18)", border: "1px solid rgba(249,115,22,0.6)", color: "#f97316" }
                : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }}
            >
              TODOS ({data.alertas_activas.length})
            </button>
            {tipos.map((t) => {
              const n = (data.alertas_activas || []).filter((a) => a.tipo === t).length;
              const active = filterTipo === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFilterTipo(t)}
                  data-testid={`admin-alerts-filter-${t}`}
                  className="rounded-md px-2.5 py-1 font-mono text-[9px] font-bold transition-all"
                  style={active
                    ? { background: "rgba(249,115,22,0.18)", border: "1px solid rgba(249,115,22,0.6)", color: "#f97316" }
                    : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }}
                >
                  {t} ({n})
                </button>
              );
            })}
          </div>
        );
      })()}

      {loading && !data && (
        <div className="flex items-center gap-2 font-mono text-[11px] text-white/55">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </div>
      )}

      {data && (data.alertas_activas || []).length === 0 && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-6 text-center">
          <ShieldAlert className="mx-auto h-8 w-8 text-[#00d084]/70" />
          <p className="mt-3 font-mono text-[11px] text-white/65">
            🟢 Sin alertas activas — todo limpio.
          </p>
        </div>
      )}

      {data && (data.alertas_activas || []).length > 0 && (() => {
        const items = filterTipo
          ? (data.alertas_activas || []).filter((a) => a.tipo === filterTipo)
          : (data.alertas_activas || []);
        return (
        <ul className="overflow-hidden rounded-lg border border-white/10">
          {items.map((a) => {
            const sev = SEV[(a.severidad || "media").toLowerCase()] || SEV.media;
            const isProSinPago = a.tipo === "pro_sin_pago";
            return (
              <li
                key={a.id}
                data-testid={`admin-alerts-row-${a.id}`}
                className="border-b border-white/5 px-3 py-2.5 font-mono last:border-b-0"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block rounded px-1.5 py-[1px] text-[8px] font-bold uppercase tracking-wider"
                      style={{ background: sev.bg, color: sev.fg }}
                    >
                      {a.severidad}
                    </span>
                    <span className="text-[10px] font-bold text-white/85">{a.tipo}</span>
                    <span className="text-[10px] text-white/45">{fmtDate(a.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isProSinPago && (
                      <button
                        type="button"
                        onClick={() => handleAction(a.id, "revoke")}
                        disabled={acting === a.id}
                        data-testid={`admin-alerts-revoke-${a.id}`}
                        title="Quita is_pro:true al usuario y resuelve la alerta"
                        className="inline-flex items-center gap-1 rounded border border-[#ff3b5c]/40 bg-[#ff3b5c]/[0.08] px-2 py-[2px] text-[9px] font-bold text-[#ff3b5c] hover:bg-[#ff3b5c]/[0.15] disabled:opacity-50"
                      >
                        <Trash2 className="h-3 w-3" /> Auto-revocar PRO
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleAction(a.id, "resolve")}
                      disabled={acting === a.id}
                      data-testid={`admin-alerts-resolve-${a.id}`}
                      title="Marca esta alerta como resuelta sin tocar al usuario"
                      className="inline-flex items-center gap-1 rounded border border-white/15 bg-white/[0.04] px-2 py-[2px] text-[9px] font-bold text-white/75 hover:border-white/40 disabled:opacity-50"
                    >
                      <Check className="h-3 w-3" /> Resolver
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-white/75">{a.descripcion}</p>
                {a.user_id && (
                  <p className="mt-0.5 text-[9px] text-white/35">
                    user_id: <code>{a.user_id}</code>
                    {a.ip ? <> · ip: <code>{a.ip}</code></> : null}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
        );
      })()}
    </div>
  );
}
